import { useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { SettingsProvider } from "@/shared/api/SettingsProvider";
import { I18nProvider } from "@/shared/i18n";
import { ToastProvider } from "@/shared/ui";
import { AppShell, type TabKey } from "@/shared/ui/AppShell";
import { LandingPage } from "@/features/landing/LandingPage";
import { HumanizePage } from "@/features/humanize/HumanizePage";
import { DetectPage } from "@/features/detect/DetectPage";
import { track } from "@/shared/analytics";
import { BillingPage } from "@/features/billing/BillingPage";
import { AccountPage } from "@/features/account/AccountPage";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
  },
});

function Console() {
  const [tab, setTabState] = useState<TabKey>("humanize");
  const setTab = (t: TabKey) => {
    track("tab_viewed", { tab: t });
    setTabState(t);
  };
  // Text handed from the detector to the rewriter. `n` bumps on every
  // hand-off so sending the same text twice still re-seeds the editor.
  const [seed, setSeed] = useState<{ text: string; n: number } | null>(null);
  const humanizeFrom = (text: string) => {
    setSeed((s) => ({ text, n: (s?.n ?? 0) + 1 }));
    setTab("humanize");
  };
  return (
    <AppShell tab={tab} onTab={setTab}>
      <div hidden={tab !== "humanize"}>
        <HumanizePage seed={seed} onGoAccount={() => setTab("account")} />
      </div>
      <div hidden={tab !== "detect"}>
        <DetectPage onHumanize={humanizeFrom} onGoAccount={() => setTab("account")} />
      </div>
      {tab === "billing" && <BillingPage />}
      {tab === "account" && <AccountPage />}
    </AppShell>
  );
}

export default function App() {
  // Path routing: "/" is the marketing landing page, "/app" the console.
  // nginx rewrites every path to index.html, so a plain <a href="/app"> works.
  const isApp = window.location.pathname.startsWith("/app");
  return (
    <QueryClientProvider client={queryClient}>
      <SettingsProvider>
        <I18nProvider>
          <ToastProvider>{isApp ? <Console /> : <LandingPage />}</ToastProvider>
        </I18nProvider>
      </SettingsProvider>
    </QueryClientProvider>
  );
}
